import React, { useEffect, useState } from 'react';
import '../../styles/onboardingFlow/onboarding/onboardingBusinessType.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import BusinessTypeSelector from '../onboardingComponents/BusinessTypeSelector';
import AIPhoneInput from '../../ui/inputs/AIPhoneInput';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import HandWavingIcon from '../../assets/onboarding/common/HandWaving.svg';
import LightBulbIcon from '../../assets/onboarding/common/light-bulb.svg';
import OnboardingLogoPanel from '../onboardingComponents/OnboardingLogoPanel';
import OnboardingFooter from '../onboardingComponents/OnboardingFooter';
import {
  setAiOnboardingNextStep,
  setAiOnboardingStepCompleted,
  setCompanyStepDraft,
  updateProfile,
} from '../../store/reducers/userSlice';
import { asyncHandler } from '../../lib/api/async-handler';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { REGISTRATION_API } from '../../lib/constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { submitLogToBugsnag } from '../../lib/api/log';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import getCountryByDialCode from '../../lib/helper-functions/getCountryByDialCode';
import Popup from '../../ui/popup/Popup';

const OnboardingBusinessType: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const seller_id = useSelector(
    (state: RootState) => state.user.profile.seller_id
  );
  const autoSelectAgencyRole = useSelector(
    (state: RootState) => state.UI.autoSelectAgencyRole
  );
  const preUserInvitation = useSelector(
    (state: RootState) => state.user.preUserInvitation
  );
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const IPCountry = useSelector(
    (state: RootState) => state.app.countries.countryOfIP
  );
  const countries = useSelector(
    (state: RootState) => state.app.countries.available
  );
  const appLanguage = useSelector((state: RootState) => state.app.language);
  const merchantSlug = useSelector(
    (state: RootState) => state.user.merchant.merchant_slug
  );
  const companyStepDraft = useSelector((state: RootState) => {
    const aiOnboarding = state.user.ai_onboarding as {
      companyStepDraft?: {
        userRole: 'seller' | 'agency';
      } | null;
    };
    return aiOnboarding.companyStepDraft ?? null;
  });
  const allShopsFromProfile = useSelector(
    (state: RootState) => state.user.profile.all_shops
  );

  const getInitialRole = (): 'seller' | 'agency' => {
    const profileRole = userProfile?.role;
    if (profileRole === 'agency' || profileRole === 'seller')
      return profileRole;
    if (companyStepDraft?.userRole) return companyStepDraft.userRole;
    return autoSelectAgencyRole ? 'agency' : 'seller';
  };

  const [loading, setLoading] = useState<'pending' | 'initial'>('initial');
  const [userRole, setUserRole] = useState<'seller' | 'agency'>(
    getInitialRole()
  );
  const [phoneNumber, setPhoneNumber] = useState({
    number: userProfile.phone,
    country: userProfile.country_code
      ? getCountryByDialCode(userProfile.country_code)
      : countries[IPCountry],
  });
  const [showCannotSwitchPopup, setShowCannotSwitchPopup] = useState(false);

  const hasShopsInProfile =
    !!allShopsFromProfile &&
    Array.isArray(allShopsFromProfile) &&
    allShopsFromProfile.length > 0;
  const disabledRoleTypes: ('business' | 'agency')[] = hasShopsInProfile
    ? userRole === 'seller'
      ? ['agency']
      : ['business']
    : [];

  useEffect(() => {
    const profileRole = userProfile?.role;
    if (profileRole === 'agency' || profileRole === 'seller') {
      setUserRole(profileRole);
    }
  }, [userProfile?.role]);

  useEffect(() => {
    if (preUserInvitation.id && preUserInvitation.data) {
      if (preUserInvitation.data?.seller_type) {
        const role =
          preUserInvitation.data?.seller_type === 'agency'
            ? 'agency'
            : 'seller';
        setUserRole(role);
      }
    }
  }, [preUserInvitation]);

  useEffect(() => {
    dispatch(
      setCompanyStepDraft({
        userRole,
      })
    );
  }, [userRole, dispatch]);

  async function submitProfile() {
    const sendingData = {
      contact: phoneNumber.number,
      country_code: phoneNumber.country?.dialCode?.substring(1),
      language: isNotEmptyString(userProfile?.language)
        ? userProfile?.language
        : isNotEmptyString(appLanguage)
        ? appLanguage
        : 'en',
    };
    try {
      const requestInfo: RequestInfo = {
        url: `${REGISTRATION_API(merchantSlug)}/${seller_id}`,
        method: 'PUT',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: `${accessToken.type} ${accessToken.sellerToken}`,
        },
      };

      const actionType = 'ONBOARDING_PROFILE_UPDATE';
      const customMsg = 'Profile Update Successful.';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        false,
        true
      );

      const response = res.data;
      if (res.actionType === actionType && response.success === true) {
        const { user } = response?.data;
        if (user?.seller_id) {
          dispatch(
            updateProfile({
              role: userRole,
              country_code: phoneNumber.country?.dialCode?.substring(1),
              phone: phoneNumber.number,
              language: isNotEmptyString(userProfile?.language)
                ? userProfile?.language
                : isNotEmptyString(appLanguage)
                ? appLanguage
                : 'en',
            })
          );
          return true;
        }
      } else {
        setLoading('initial');
        return false;
      }
    } catch (exception) {
      setLoading('initial');
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.number || !isNotEmptyString(phoneNumber.number)) {
      return;
    }

    setLoading('pending');

    try {
      const profileUpdated = await submitProfile();
      if (!profileUpdated) throw new Error('Profile update failed');

      setLoading('initial');
      dispatch(
        setAiOnboardingStepCompleted({ stepNumber: 1, isCompleted: true })
      );
      dispatch(setAiOnboardingNextStep(2));
    } catch (error) {
      console.error('Error during submission:', error);
      setLoading('initial');
    }
  };

  const isDisabled =
    !phoneNumber.number || !isNotEmptyString(phoneNumber.number);

  return (
    <form onSubmit={submitForm} className='onboardingBusinessType'>
      <OnboardingLogoPanel />
      <div className='onboardingBusinessType__header'>
        <div className='onboardingBusinessType__header-row'>
          <div className='onboardingBusinessType__header-icon'>
            <img src={HandWavingIcon} alt='Welcome' />
          </div>
          <h1 className='onboardingBusinessType__title'>
            Tell us about your business
          </h1>
        </div>
        <p className='onboardingBusinessType__subtitle'>
          Select your business type and add your contact details.
        </p>
      </div>

      <BusinessTypeSelector
        selectedType={userRole === 'seller' ? 'business' : 'agency'}
        onSelect={(type) =>
          setUserRole(type === 'business' ? 'seller' : 'agency')
        }
        disabledTypes={disabledRoleTypes}
        onDisabledTypeClick={() => setShowCannotSwitchPopup(true)}
      />

      <div className='onboardingBusinessType__section'>
        <h2 className='onboardingBusinessType__section-title'>
          Contact number
        </h2>
        <div className='onboardingBusinessType__input'>
          <AIPhoneInput
            id='onboardingBusinessType__user-phone-input'
            setPhoneNumber={setPhoneNumber}
            phoneNumber={phoneNumber}
            label=''
            placeholder='Enter your phone number'
            title={t('Phone-validation')}
            required={true}
            pattern='^[0-9]{4,}$'
          />
        </div>
      </div>

      <div className='onboardingBusinessType__footer'>
        <MainSquareButton
          type='submit'
          value='Continue'
          normal={true}
          loading={loading === 'pending'}
          disabled={isDisabled}
          style={{
            width: '100%',
            paddingTop: '1.2rem',
            paddingBottom: '1.2rem',
          }}
        />
      </div>

      <OnboardingFooter />

      <Popup
        trigger={showCannotSwitchPopup}
        setTrigger={() => setShowCannotSwitchPopup(false)}
      >
        <div className='onboardingBusinessType__cannot-switch-popup'>
          <div className='onboardingBusinessType__cannot-switch-popup-header'>
            <img
              src={LightBulbIcon}
              alt=''
              className='onboardingBusinessType__cannot-switch-popup-icon'
            />
            <h3 className='onboardingBusinessType__cannot-switch-popup-title'>
              Can't switch
            </h3>
          </div>
          <div className='onboardingBusinessType__cannot-switch-popup-divider' />
          <div className='onboardingBusinessType__cannot-switch-popup-body'>
            <p className='onboardingBusinessType__cannot-switch-popup-text'>
              After creating a shop, you cannot switch between Business and
              Agency.
            </p>
            <button
              type='button'
              className='onboardingBusinessType__cannot-switch-popup-button'
              onClick={() => setShowCannotSwitchPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      </Popup>
    </form>
  );
};

export default OnboardingBusinessType;
